import React, { useRef, useEffect, useState, ReactNode, useCallback } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { isTabRoot } from '@/components/layout/navRoutes';
import { releaseVelocity, shouldCompleteSwipe, swipeIntent, type TouchSample } from './swipeBack';

interface SwipeablePageWrapperProps {
  children: ReactNode;
}

// Global page cache and history
const pageCache = new Map<string, ReactNode>();
let historyStack: string[] = [];

export function SwipeablePageWrapper({ children }: SwipeablePageWrapperProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const containerRef = useRef<HTMLDivElement>(null);

  // Motion value for current page position
  const x = useMotionValue(0);
  const [screenWidth, setScreenWidth] = useState(375);
  const [isDragging, setIsDragging] = useState(false);

  // Keep cached page visible during transition to prevent flash
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionPreviousPage, setTransitionPreviousPage] = useState<ReactNode>(null);

  // Refs for touch tracking
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwipeActive = useRef(false);
  // Set once the touch is clearly a sideways swipe; until then the page doesn't move.
  const isDirectionLocked = useRef(false);
  // True only while navigating after a completed swipe (the page is off screen).
  const isNavigating = useRef(false);
  // Last few touch positions, for the release velocity.
  const samples = useRef<TouchSample[]>([]);
  // The running spring, so a touch can grab the page mid-flight.
  const animation = useRef<{ stop: () => void } | null>(null);

  const isIOS = Capacitor.getPlatform() === 'ios';

  // Update screen width
  useEffect(() => {
    setScreenWidth(window.innerWidth);
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track history and cache pages
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const lastPath = historyStack[historyStack.length - 1];

    if (lastPath !== currentPath) {
      // Only trim history on actual back navigation (POP), not on forward navigation (PUSH)
      // This fixes the bug where navigating to a page already in history via button click
      // would incorrectly trim the history stack
      if (navigationType === 'POP') {
        // Browser back/forward or swipe-back - find position in history
        const existingIndex = historyStack.indexOf(currentPath);
        if (existingIndex >= 0) {
          historyStack = historyStack.slice(0, existingIndex + 1);
        } else {
          // Edge case: POP to unknown page, just add it
          historyStack.push(currentPath);
        }
      } else {
        // PUSH or REPLACE - always add to history (allows going back)
        historyStack.push(currentPath);
      }

      // Limit history
      while (historyStack.length > 15) {
        const removed = historyStack.shift();
        if (removed) pageCache.delete(removed);
      }
    }

    // Always cache current page content
    pageCache.set(currentPath, children);
  }, [location.pathname, location.search, navigationType, children]);

  // Get previous page for rendering - handlers read historyStack directly to avoid stale closures
  const canGoBack = historyStack.length > 1;
  const previousPath = canGoBack ? historyStack[historyStack.length - 2] : null;
  const previousPage = previousPath ? pageCache.get(previousPath) : null;

  // Transforms for parallax effect
  const backgroundX = useTransform(x, [0, screenWidth], [-screenWidth * 0.3, 0]);
  const overlayOpacity = useTransform(x, [0, screenWidth], [0.4, 0]);

  // Touch handlers for edge swipe-back. They read historyStack directly (not
  // via state) to avoid stale closures.
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (isNavigating.current || historyStack.length <= 1) return;
    if (isTabRoot(window.location.pathname)) return; // tab roots: nothing to swipe back to
    // A sheet or dialog is on top: the drag belongs to it, not to the page underneath.
    if (document.querySelector('[data-vaul-drawer][data-state="open"], [role="dialog"][data-state="open"]')) return;

    const touch = e.touches[0];
    const EDGE_THRESHOLD = 25;
    const offset = x.get();

    if (animation.current && offset > 0) {
      // Grab the page mid-flight and keep tracking from where it is now.
      animation.current.stop();
      animation.current = null;
      touchStartX.current = touch.clientX - offset;
      isDirectionLocked.current = true;
    } else if (touch.clientX <= EDGE_THRESHOLD) {
      touchStartX.current = touch.clientX;
      isDirectionLocked.current = false;
    } else {
      return;
    }
    touchStartY.current = touch.clientY;
    samples.current = [{ x: touch.clientX, t: e.timeStamp }];
    isSwipeActive.current = true;
  }, [x]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isSwipeActive.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = Math.abs(touch.clientY - touchStartY.current);

    if (!isDirectionLocked.current) {
      const intent = swipeIntent(deltaX, deltaY);
      if (intent === 'pending') return; // don't move the page until the direction is clear
      if (intent === 'none') {
        // A vertical scroll (or leftward drag) that started near the edge: let it be.
        isSwipeActive.current = false;
        samples.current = [];
        return;
      }
      isDirectionLocked.current = true;
      setIsDragging(true); // only now mount the previous page underneath
    }

    samples.current.push({ x: touch.clientX, t: e.timeStamp });
    if (samples.current.length > 5) samples.current.shift();
    x.set(Math.max(0, deltaX));
  }, [x]);

  const handleTouchEnd = useCallback(() => {
    if (!isSwipeActive.current) return;
    isSwipeActive.current = false;

    const velocity = releaseVelocity(samples.current);

    if (shouldCompleteSwipe(x.get(), velocity, screenWidth) && historyStack.length > 1) {
      // Complete: continue at the finger's speed, then navigate.
      const prevPath = historyStack[historyStack.length - 2];
      const cachedPage = prevPath ? pageCache.get(prevPath) : null;

      animation.current = animate(x, screenWidth, {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        velocity,
        onComplete: () => {
          animation.current = null;
          isNavigating.current = true;
          // Keep showing the cached previous page while the real one mounts.
          if (cachedPage) {
            setTransitionPreviousPage(cachedPage);
            setIsTransitioning(true);
          }
          setIsDragging(false);
          historyStack.pop();
          x.set(0);
          navigate(-1);
          setTimeout(() => {
            setIsTransitioning(false);
            setTransitionPreviousPage(null);
            isNavigating.current = false;
          }, 150);
        },
      });
    } else {
      // Cancel: spring back from wherever it is, carrying the finger's speed.
      animation.current = animate(x, 0, {
        type: 'spring',
        stiffness: 400,
        damping: 35,
        velocity,
        onComplete: () => {
          animation.current = null;
          setIsDragging(false);
        },
      });
    }
  }, [x, screenWidth, navigate]);

  // The system took the touch (call, alert, notification centre): spring back.
  const handleTouchCancel = useCallback(() => {
    if (!isSwipeActive.current) return;
    isSwipeActive.current = false;
    animation.current = animate(x, 0, {
      type: 'spring',
      stiffness: 400,
      damping: 35,
      onComplete: () => {
        animation.current = null;
        setIsDragging(false);
      },
    });
  }, [x]);

  // Attach touch listeners. On the document, and not gated on the wrapper being
  // mounted: tab roots render unwrapped, and the handlers already ignore them.
  useEffect(() => {
    if (!isIOS) return;

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [isIOS, handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);

  // Non-iOS, and tab roots (nothing to swipe back to): render children unwrapped.
  // Skipping the wrapper also drops its z-index stacking context there.
  if (!isIOS || isTabRoot(location.pathname)) {
    return <>{children}</>;
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full min-h-full"
      style={{ overflow: 'hidden' }}
    >
      {/* Previous page (background) - only render when dragging */}
      {isDragging && previousPage && (
        <motion.div
          className="absolute inset-0"
          style={{
            x: backgroundX,
            zIndex: 0,
            background: 'var(--background, #f8fafc)'
          }}
        >
          <div className="w-full h-full overflow-hidden">
            {previousPage}
          </div>
        </motion.div>
      )}

      {/* Dark overlay between pages */}
      {isDragging && (
        <motion.div
          className="absolute inset-0 bg-black pointer-events-none"
          style={{
            opacity: overlayOpacity,
            zIndex: 1
          }}
        />
      )}

      {/* Current page */}
      <motion.div
        className="relative w-full min-h-full"
        style={{
          x,
          zIndex: 2,
          background: 'var(--background, #f8fafc)',
          boxShadow: isDragging ? '-8px 0 25px rgba(0, 0, 0, 0.15)' : 'none'
        }}
      >
        {children}
      </motion.div>

      {/* Transition overlay - shows cached page while real page mounts */}
      {isTransitioning && transitionPreviousPage && (
        <div
          className="absolute inset-0"
          style={{
            zIndex: 10,
            background: 'var(--background, #f8fafc)'
          }}
        >
          {transitionPreviousPage}
        </div>
      )}
    </div>
  );
}
